// Temporary bridge between a fleet-authored HTML document running in the
// sandboxed iframe (opaque origin) and the quote-review page. The script below
// is injected into the iframe's srcdoc; it reports what the renter typed with
// `postMessage`, and the page validates the sender (`event.source`) before
// trusting it. See odd/tasks/document-form-capture.md.

export const DOCUMENT_FORM_MESSAGE_TYPE = "fm-document-form";

export type DocumentFormValues = Record<string, string>;

export interface DocumentFormStatus {
  values: DocumentFormValues;
  /** Absent when the bridge did not report it (old bridge): fail open. */
  complete?: boolean;
  missing?: number;
}

export function parseDocumentFormMessage(
  data: unknown,
): DocumentFormValues | null {
  return parseDocumentFormStatusMessage(data)?.values ?? null;
}

export function parseDocumentFormStatusMessage(
  data: unknown,
): DocumentFormStatus | null {
  if (typeof data !== "object" || data === null) return null;
  const { type, values, complete, missing } = data as {
    type?: unknown;
    values?: unknown;
    complete?: unknown;
    missing?: unknown;
  };
  if (type !== DOCUMENT_FORM_MESSAGE_TYPE) return null;
  if (typeof values !== "object" || values === null || Array.isArray(values)) {
    return null;
  }

  const clean: DocumentFormValues = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === "string") clean[key] = value;
  }
  const status: DocumentFormStatus = { values: clean };
  if (typeof complete === "boolean") status.complete = complete;
  if (
    typeof missing === "number" &&
    Number.isInteger(missing) &&
    missing >= 0
  ) {
    status.missing = missing;
  }
  return status;
}

// Plain ES5 on purpose: it runs inside documents we do not control.
export const DOCUMENT_FORM_BRIDGE_SCRIPT = `
(function () {
  var SKIPPED = { password: 1, file: 1, submit: 1, button: 1, reset: 1, image: 1, hidden: 1 };
  var timer = null;

  function isBlank(canvas) {
    var empty = document.createElement("canvas");
    empty.width = canvas.width;
    empty.height = canvas.height;
    return empty.toDataURL() === canvas.toDataURL();
  }

  function collect() {
    var values = {};
    var fields = document.querySelectorAll("input, textarea, select");
    for (var i = 0; i < fields.length; i += 1) {
      var el = fields[i];
      var key = el.name || el.id;
      var type = (el.type || "").toLowerCase();
      if (!key || SKIPPED[type]) continue;
      if (type === "radio") {
        if (el.checked) values[key] = el.value;
      } else if (type === "checkbox") {
        var suffix = el.name && el.value && el.value !== "on" ? ":" + el.value : "";
        values[key + suffix] = el.checked ? "true" : "false";
      } else {
        values[key] = el.value;
      }
    }
    var canvases = document.querySelectorAll("canvas");
    for (var j = 0; j < canvases.length; j += 1) {
      try {
        if (!isBlank(canvases[j])) {
          values["canvas:" + (canvases[j].id || j)] = canvases[j].toDataURL("image/png");
        }
      } catch (e) {}
    }
    return values;
  }

  // Number of required controls and signature canvases still unfilled.
  function countMissing() {
    var missing = 0;
    var seenGroups = {};
    var fields = document.querySelectorAll("input, textarea, select");
    for (var i = 0; i < fields.length; i += 1) {
      var el = fields[i];
      if (el.disabled || typeof el.checkValidity !== "function") continue;
      if (el.checkValidity()) continue;
      var type = (el.type || "").toLowerCase();
      if (type === "radio" && el.name) {
        if (seenGroups[el.name]) continue;
        seenGroups[el.name] = true;
      }
      missing += 1;
    }
    var canvases = document.querySelectorAll("canvas[data-required]");
    for (var j = 0; j < canvases.length; j += 1) {
      try {
        if (isBlank(canvases[j])) missing += 1;
      } catch (e) {}
    }
    return missing;
  }

  function send() {
    var missing = countMissing();
    parent.postMessage({
      type: "${DOCUMENT_FORM_MESSAGE_TYPE}",
      values: collect(),
      complete: missing === 0,
      missing: missing
    }, "*");
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(send, 200);
  }

  ["input", "change", "keyup", "pointerup", "mouseup", "touchend"].forEach(function (name) {
    document.addEventListener(name, schedule, true);
  });
  document.addEventListener("submit", function (event) {
    event.preventDefault();
    send();
  }, true);

  // Report once the document is ready (documents without required fields are
  // complete without any interaction) and again when every resource loaded.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", send);
  } else {
    send();
  }
  window.addEventListener("load", send);
})();
`;
